import type { LayoutTemplate } from './types';

const DEFAULT_KARAOKE_DISPLAY_ELEMENT = {
  type: 'text',
};

/**
 * Preset Layout Templates
 * Defines commonly used layout templates (aspect-ratio-agnostic)
 */

export enum TemplateIds {
  Fill = 'fill',
  Cover = 'cover',
  Contain = 'contain',
  SplitVertical = 'split_vertical',
  SplitHorizontal = 'split_horizontal',
  Stacked1To3 = 'stacked_horizontal_1_3',
  BlurredBackground = 'blurred_background',
  ZoomCall = 'zoom_call',
}

// ============================================================================
// BASIC FIT LAYOUTS
// ============================================================================

const fillTemplate: LayoutTemplate = {
  id: TemplateIds.Fill,
  type: 'basic',
  name: 'Fill',
  description: 'Single element fills the entire frame',
  slots: ['main', 'karaoke'],
  layoutElements: [
    {
      name: 'main',
      displayElement: {
        type: 'video',
        fit: 'fill',
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

const coverTemplate: LayoutTemplate = {
  id: TemplateIds.Cover,
  type: 'basic',
  name: 'Cover',
  description: 'Single element covers the entire frame with cropping',
  slots: ['main', 'karaoke'],
  layoutElements: [
    {
      name: 'main',
      displayElement: {
        type: 'video',
        fit: 'cover',
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

const containTemplate: LayoutTemplate = {
  id: TemplateIds.Contain,
  type: 'basic',
  name: 'Contain',
  description: 'Single element contained within frame with letterboxing',
  slots: ['main', 'karaoke'],
  layoutElements: [
    {
      name: 'main',
      displayElement: {
        type: 'video',
        fit: 'contain',
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

// ============================================================================
// SPLIT LAYOUTS - Aspect-agnostic split layouts
// ============================================================================

const splitVerticalTemplate: LayoutTemplate = {
  id: TemplateIds.SplitVertical,
  type: 'split',
  name: 'Split Vertical 1:1',
  description: 'Two elements side by side (50/50 split)',
  slots: ['left', 'right', 'karaoke'],
  layoutElements: [
    {
      name: 'left',
      displayElement: {
        type: 'video',
      },
      container: {
        x: '25%',
        y: '50%',
        width: '50%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'right',
      displayElement: {
        type: 'video',
        volume: '0%', // needed to prevent duplicating sound
      },
      container: {
        x: '75%',
        y: '50%',
        width: '50%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

const splitHorizontalTemplate: LayoutTemplate = {
  id: TemplateIds.SplitHorizontal,
  type: 'split',
  name: 'Split Horizontal 1:1 (stacked-1-1)',
  description: 'Two elements top and bottom (50/50 split)',
  slots: ['top', 'bottom', 'karaoke'],
  layoutElements: [
    {
      name: 'top',
      displayElement: {
        type: 'video',
      },
      container: {
        x: '50%',
        y: '25%',
        width: '100%',
        height: '50%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'bottom',
      displayElement: {
        type: 'video',
        volume: '0%', // needed to prevent duplicating sound
      },
      container: {
        x: '50%',
        y: '75%',
        width: '100%',
        height: '50%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        type: 'text',
        x: '50%',
        y: '50%',
        x_alignment: '50%',
      },
      container: null,
    },
  ],
};

const stacked1To3Template: LayoutTemplate = {
  id: TemplateIds.Stacked1To3,
  type: 'split',
  name: 'Split Horizontal 1:3 (stacked-1-3)',
  description: 'Two elements top and bottom (33/66 split)',
  slots: ['top', 'bottom', 'karaoke'],
  layoutElements: [
    {
      name: 'top',
      displayElement: {
        type: 'video',
      },
      container: {
        x: '50%',
        y: '16.67%',
        width: '100%',
        height: '33.33%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'bottom',
      displayElement: {
        type: 'video',
        volume: '0%', // needed to prevent duplicating sound
      },
      container: {
        x: '50%',
        y: '66.67%',
        width: '100%',
        height: '66.67%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        type: 'text',
        x: '50%',
        y: '33.333%',
        x_alignment: '50%',
      },
      container: null,
    },
  ],
};

const blurredBackgroundTemplate: LayoutTemplate = {
  id: TemplateIds.BlurredBackground,
  type: 'basic',
  name: 'Blurred Background',
  description: 'Single element with blurred background to fill letterbox areas',
  slots: ['foreground', 'background', 'karaoke'],
  layoutElements: [
    {
      name: 'background',
      displayElement: {
        type: 'video',
        blur_radius: 50,
        volume: '0%',
        locked: true,
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'foreground',
      displayElement: {
        type: 'video',
        fit: 'contain',
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '100%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

// ============================================================================
// EXPERIMENTAL: Zoom Call
// ============================================================================

/**
 * Zoom-call layout: a single 16:9 band that occupies the full width and
 * exactly (9/16)² = 31.640625% of the height of a 9:16 portrait frame,
 * centred vertically. Per-scene crop targets the active speaker's face.
 */
const zoomCallTemplate: LayoutTemplate = {
  id: TemplateIds.ZoomCall,
  type: 'basic',
  name: 'Zoom Call',
  description: 'Active speaker shown in a 16:9 band, centred in the 9:16 frame',
  slots: ['main', 'karaoke'],
  layoutElements: [
    {
      name: 'main',
      displayElement: {
        type: 'video',
        fit: 'cover',
      },
      container: {
        x: '50%',
        y: '50%',
        width: '100%',
        height: '31.640625%',
        x_anchor: '50%',
        y_anchor: '50%',
        x_alignment: '50%',
        y_alignment: '50%',
      },
    },
    {
      name: 'karaoke',
      displayElement: {
        ...DEFAULT_KARAOKE_DISPLAY_ELEMENT,
      },
      container: null,
    },
  ],
};

// ============================================================================
// Export all preset templates
// ============================================================================

export const presetTemplates: LayoutTemplate[] = [
  fillTemplate,
  coverTemplate,
  containTemplate,
  splitVerticalTemplate,
  splitHorizontalTemplate,
  stacked1To3Template,
  blurredBackgroundTemplate,
  zoomCallTemplate,
];
